import api, { isApiError } from '@/services/api';
import type {
    AbsorbedFeeLine,
    AbsorbedFees,
    AgreementFinancialsResult,
    PlanCost,
    PlanCostAmounts,
    PlanCostLine,
    PlanCostLineKey,
    RelatedRow,
    ScheduledPayments,
    ScheduledState,
    SplitState,
    StatementRow,
    StatementRowKind,
    StatementSection,
    StatementTotals,
} from '../types/financials.types';

const NOT_ACTIVATED_CODE = 'agreement_not_activated';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MONEY_2DP = /^-?\d+\.\d{2}$/;
const SECTIONS: readonly StatementSection[] = ['collected_in', 'paid_out', 'scheduled_in', 'scheduled_out'];
const KINDS: readonly StatementRowKind[] = ['entry', 'service', 'plan_fees', 'other'];
const SCHEDULED_STATUSES: readonly ScheduledPayments['status'][] = ['available', 'unavailable', 'not_applicable'];
const PLAN_COST_KEYS: readonly PlanCostLineKey[] = ['services', 'add_ons', 'fee', 'plan_fees'];
const ABSORBED_STATUSES: readonly AbsorbedFees['status'][] = ['available', 'unavailable'];
const SPLIT_STATES: readonly SplitState[] = ['recorded', 'unsplit'];
const STATES: readonly ScheduledState[] = ['settled', 'scheduled', 'overdue', 'held'];

const malformed = (detail: string): Error => new Error(`Malformed financials payload: ${detail}`);

const readRecord = (value: unknown, label: string): Record<string, unknown> => {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
        throw malformed(`${label} is not an object`);
    }
    return value as Record<string, unknown>;
};

const readList = (value: unknown, label: string): unknown[] => {
    if (value === undefined || value === null) return [];
    if (!Array.isArray(value)) throw malformed(`${label} is not an array`);
    return value;
};

const readText = (value: unknown, label: string): string => {
    if (typeof value !== 'string') throw malformed(`${label} is not a string`);
    return value;
};

const readDate = (value: unknown, label: string): string => {
    const text = readText(value, label);
    if (!ISO_DATE.test(text)) throw malformed(`${label} is not a date`);
    return text;
};

const readNullableText = (value: unknown, label: string): string | null =>
    value === undefined || value === null ? null : readText(value, label);

const readMoney = (value: unknown, label: string): string => {
    if (typeof value !== 'string' || !MONEY_2DP.test(value)) throw malformed(`${label} is not a 2dp amount`);
    return value;
};

const readBoolean = (value: unknown, label: string): boolean => {
    if (typeof value !== 'boolean') throw malformed(`${label} is not a boolean`);
    return value;
};

const readOneOf = <T extends string>(value: unknown, allowed: readonly T[], label: string): T => {
    if (typeof value !== 'string' || !allowed.includes(value as T)) throw malformed(`unknown ${label}`);
    return value as T;
};

const readCount = (value: unknown): number => {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
        throw malformed('scheduled_payments count is not a non-negative integer');
    }
    return value;
};

const readSequence = (value: unknown): number | null => {
    if (value === undefined || value === null) return null;
    if (typeof value !== 'number' || !Number.isInteger(value)) throw malformed('service_sequence is not an integer');
    return value;
};

const readSplitState = (value: unknown, state: ScheduledState): SplitState | null => {
    if (value === undefined || value === null) return null;
    if (state !== 'settled') throw malformed('row split_state on a non-settled row');
    return readOneOf(value, SPLIT_STATES, 'row split_state');
};

const readSplit = (raw: Record<string, unknown>): Pick<StatementRow, 'net' | 'vat'> => {
    if (raw.net === undefined && raw.vat === undefined) return { net: null, vat: null };
    return { net: readMoney(raw.net, 'row net'), vat: readMoney(raw.vat, 'row vat') };
};

const mapRow = (value: unknown): StatementRow => {
    const raw = readRecord(value, 'row');
    const state = readOneOf(raw.state, STATES, 'row state');
    return {
        date: readDate(raw.date, 'row date'),
        section: readOneOf(raw.section, SECTIONS, 'row section'),
        kind: readOneOf(raw.kind, KINDS, 'row kind'),
        description: readText(raw.description, 'row description'),
        amount: readMoney(raw.amount, 'row amount'),
        ...readSplit(raw),
        splitState: readSplitState(raw.split_state, state),
        state,
        balanceAfter: raw.balance_after === undefined ? null : readMoney(raw.balance_after, 'balance_after'),
        transactionCode: readNullableText(raw.transaction_code, 'transaction_code'),
        serviceSequence: readSequence(raw.service_sequence),
    };
};

const mapRelated = (value: unknown): RelatedRow => {
    const raw = readRecord(value, 'related row');
    return {
        date: readDate(raw.date, 'related date'),
        description: readText(raw.description, 'related description'),
        amount: readMoney(raw.amount, 'related amount'),
    };
};

const mapTotals = (value: unknown): StatementTotals => {
    const raw = readRecord(value, 'totals');
    return {
        collectedIn: readMoney(raw.collected_in, 'collected_in'),
        paidOut: readMoney(raw.paid_out, 'paid_out'),
        scheduledIn: readMoney(raw.scheduled_in, 'scheduled_in'),
        scheduledOut: readMoney(raw.scheduled_out, 'scheduled_out'),
        balance: readMoney(raw.balance, 'balance'),
    };
};

const mapPlanCostAmounts = (raw: Record<string, unknown>, label: string): PlanCostAmounts => ({
    net: readMoney(raw.net, `${label} net`),
    vat: readMoney(raw.vat, `${label} vat`),
    gross: readMoney(raw.gross, `${label} gross`),
});

const mapPlanCostLine = (value: unknown): PlanCostLine => {
    const raw = readRecord(value, 'plan_cost line');
    return {
        key: readOneOf(raw.key, PLAN_COST_KEYS, 'plan_cost line key'),
        label: readText(raw.label, 'plan_cost line label'),
        transactionCode: readNullableText(raw.transaction_code, 'plan_cost transaction_code'),
        ...mapPlanCostAmounts(raw, 'plan_cost line'),
    };
};

const mapPlanCost = (value: unknown): PlanCost => {
    const raw = readRecord(value, 'plan_cost');
    if (!Array.isArray(raw.lines)) throw malformed('plan_cost lines is not an array');
    return {
        lines: raw.lines.map(mapPlanCostLine),
        total: mapPlanCostAmounts(readRecord(raw.total, 'plan_cost total'), 'plan_cost total'),
    };
};

const mapScheduledPayments = (value: unknown): ScheduledPayments => {
    const raw = readRecord(value, 'scheduled_payments');
    const status = readOneOf(raw.status, SCHEDULED_STATUSES, 'scheduled_payments status');
    return status === 'available' ? { status, count: readCount(raw.count) } : { status };
};

const readBand = (value: unknown): number | null => {
    if (value === undefined || value === null) return null;
    if (typeof value !== 'number' || !Number.isInteger(value)) throw malformed('absorbed fee band is not an integer');
    return value;
};

const mapAbsorbedFeeLine = (value: unknown): AbsorbedFeeLine => {
    const raw = readRecord(value, 'absorbed fee line');
    return {
        productFeeId: readText(raw.product_fee_id, 'absorbed fee product_fee_id'),
        label: readText(raw.label, 'absorbed fee label'),
        transactionCode: readNullableText(raw.transaction_code, 'absorbed fee transaction_code'),
        schedulingMethod: readNullableText(raw.scheduling_method, 'absorbed fee scheduling_method'),
        valueType: readNullableText(raw.value_type, 'absorbed fee value_type'),
        percentage: readNullableText(raw.percentage, 'absorbed fee percentage'),
        band: readBand(raw.band),
        net: readMoney(raw.net, 'absorbed fee net'),
        vat: readMoney(raw.vat, 'absorbed fee vat'),
        gross: readMoney(raw.gross, 'absorbed fee gross'),
    };
};

const mapAbsorbedFees = (value: unknown): AbsorbedFees | null => {
    if (value === undefined || value === null) return null;
    const raw = readRecord(value, 'absorbed_fees');
    const status = readOneOf(raw.status, ABSORBED_STATUSES, 'absorbed_fees status');
    if (status === 'available' && !Array.isArray(raw.lines)) throw malformed('absorbed_fees lines is not an array');
    return {
        status,
        reconstructed: readBoolean(raw.reconstructed, 'absorbed_fees reconstructed'),
        lines: readList(raw.lines, 'absorbed_fees lines').map(mapAbsorbedFeeLine),
    };
};

const mapFinancials = (value: unknown): AgreementFinancialsResult => {
    const raw = readRecord(value, 'root');
    const visibility = readRecord(raw.visibility, 'visibility');
    return {
        status: 'ok',
        agreementId: readText(raw.agreement_id, 'agreement_id'),
        currency: readText(raw.currency, 'currency'),
        asOf: readDate(raw.as_of, 'as_of'),
        totals: mapTotals(raw.totals),
        planCost: mapPlanCost(raw.plan_cost),
        rows: readList(raw.rows, 'rows').map(mapRow),
        related: readList(raw.related, 'related').map(mapRelated),
        visibility: {
            detail: readBoolean(visibility.detail, 'visibility detail'),
            degraded: readBoolean(visibility.degraded, 'visibility degraded'),
        },
        truncated: readBoolean(raw.truncated, 'truncated'),
        scheduledPayments: mapScheduledPayments(raw.scheduled_payments),
        absorbedFees: mapAbsorbedFees(raw.absorbed_fees),
    };
};

const isNotActivated = (error: unknown): boolean =>
    isApiError(error) && error.status === 404 && error.code === NOT_ACTIVATED_CODE;

export const fetchAgreementFinancials = async (id: string): Promise<AgreementFinancialsResult> => {
    try {
        const response = await api.get(`/agreements/${id}/financials`);
        return mapFinancials(response.data?.data);
    } catch (error) {
        if (isNotActivated(error)) return { status: 'not_activated' };
        throw error;
    }
};
